import React, { useEffect, useState, useCallback } from 'react';
import { Plus, RefreshCw, X, Check, Edit3, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Administrator' },
  { value: 'PM', label: 'Projektleiter' },
  { value: 'BL', label: 'Bauleiter' },
  { value: 'MA', label: 'Mitarbeiter' },
  { value: 'SUB', label: 'Subunternehmer' }
];

const DEFAULT_MITARBEITER = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  role: 'MA',
  telefon: '',
  mobil: '',
  stundensatz: '',
  ist_aktiv_mitarbeiter: true,
  notizen: ''
};

function compareByLabelDe(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'de', { sensitivity: 'base' });
}

function MitarbeiterView({ token }) {
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingMitarbeiter, setEditingMitarbeiter] = useState(null);
  const [newMitarbeiter, setNewMitarbeiter] = useState(null);

  const loadMitarbeiter = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/core/users/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMitarbeiter(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
      alert('Fehler beim Laden der Mitarbeiter');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadMitarbeiter();
  }, [loadMitarbeiter]);

  function normalizeMitarbeiter(data) {
    return {
      id: data.id,
      username: data.username || '',
      email: data.email || '',
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      role: data.role || 'MA',
      telefon: data.telefon || '',
      mobil: data.mobil || '',
      stundensatz: data.stundensatz ?? '',
      ist_aktiv_mitarbeiter: data.ist_aktiv_mitarbeiter ?? true,
      notizen: data.notizen || ''
    };
  }

  async function startEditMitarbeiter(user) {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/users/${user.id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEditingMitarbeiter(normalizeMitarbeiter(response.data));
    } catch (error) {
      console.error('Fehler beim Laden des Mitarbeiters:', error);
      setEditingMitarbeiter(normalizeMitarbeiter(user));
    }
  }

  async function saveMitarbeiter(user) {
    const payload = {
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      telefon: user.telefon,
      mobil: user.mobil,
      stundensatz: user.stundensatz === '' ? null : Number(user.stundensatz),
      ist_aktiv_mitarbeiter: Boolean(user.ist_aktiv_mitarbeiter),
      notizen: user.notizen
    };

    try {
      if (user.id) {
        await axios.put(`${API_BASE_URL}/core/users/${user.id}/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/core/users/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setEditingMitarbeiter(null);
      setNewMitarbeiter(null);
      loadMitarbeiter();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern';
      alert(errorMsg);
    }
  }

  async function deleteMitarbeiter(id) {
    if (!window.confirm('Mitarbeiter wirklich loeschen?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/core/users/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadMitarbeiter();
    } catch (error) {
      console.error('Fehler beim Loeschen:', error);
      alert('Fehler beim Loeschen des Mitarbeiters');
    }
  }

  const filteredMitarbeiter = mitarbeiter
    .filter((m) => {
      const needle = search.trim().toLowerCase();
      const matchSearch =
        !needle ||
        (m.first_name || '').toLowerCase().includes(needle) ||
        (m.last_name || '').toLowerCase().includes(needle) ||
        (m.username || '').toLowerCase().includes(needle) ||
        (m.email || '').toLowerCase().includes(needle);
      const matchRole = !roleFilter || m.role === roleFilter;
      const matchActive = showInactive || m.ist_aktiv_mitarbeiter;
      return matchSearch && matchRole && matchActive;
    })
    .sort((a, b) => (
      compareByLabelDe(`${a.last_name || ''} ${a.first_name || ''}`.trim(), `${b.last_name || ''} ${b.first_name || ''}`.trim())
    ));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Mitarbeiter</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={loadMitarbeiter} className="btn" disabled={loading}>
            <RefreshCw size={18} className={loading ? 'spinning' : ''} /> Aktualisieren
          </button>
          {!newMitarbeiter && !editingMitarbeiter && (
            <button
              onClick={() => setNewMitarbeiter({ ...DEFAULT_MITARBEITER })}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Plus size={18} /> Neuer Mitarbeiter
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', alignItems: 'center' }}>
          <input
            type="text"
            className="input"
            placeholder="Suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <select
              className="input"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">Alle Rollen</option>
              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>Nicht Aktive auch anzeigen</span>
            </label>
          </div>
        </div>
      </div>

      {(newMitarbeiter || editingMitarbeiter) && (
        <MitarbeiterForm
          mitarbeiter={editingMitarbeiter || newMitarbeiter}
          isEditing={Boolean(editingMitarbeiter)}
          onChange={(m) => editingMitarbeiter ? setEditingMitarbeiter(m) : setNewMitarbeiter(m)}
          onSave={() => saveMitarbeiter(editingMitarbeiter || newMitarbeiter)}
          onCancel={() => {
            setEditingMitarbeiter(null);
            setNewMitarbeiter(null);
          }}
        />
      )}

      <div className="card">
        {loading && mitarbeiter.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Lade Mitarbeiter...
          </div>
        ) : filteredMitarbeiter.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Keine Mitarbeiter gefunden
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Name</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Benutzername</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>E-Mail</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Rolle</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Aktiv</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredMitarbeiter.map((m) => (
                  <tr key={m.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600 }}>
                      {m.first_name} {m.last_name}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{m.username}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{m.email || '-'}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{m.role_display || m.role}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      {m.ist_aktiv_mitarbeiter ? 'Ja' : 'Nein'}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn"
                          onClick={() => startEditMitarbeiter(m)}
                          title="Bearbeiten"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className="btn"
                          style={{ color: '#dc2626' }}
                          onClick={() => deleteMitarbeiter(m.id)}
                          title="Loeschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MitarbeiterForm({ mitarbeiter, isEditing, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '1.5rem', background: '#f9fafb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
          {isEditing ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
        </h3>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Vorname *
            </label>
            <input
              type="text"
              value={mitarbeiter.first_name}
              onChange={(e) => onChange({ ...mitarbeiter, first_name: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Nachname *
            </label>
            <input
              type="text"
              value={mitarbeiter.last_name}
              onChange={(e) => onChange({ ...mitarbeiter, last_name: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Benutzername *
            </label>
            <input
              type="text"
              value={mitarbeiter.username}
              onChange={(e) => onChange({ ...mitarbeiter, username: e.target.value })}
              className="input"
              disabled={Boolean(mitarbeiter.id)}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              E-Mail
            </label>
            <input
              type="email"
              value={mitarbeiter.email}
              onChange={(e) => onChange({ ...mitarbeiter, email: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Rolle
            </label>
            <select
              className="input"
              value={mitarbeiter.role}
              onChange={(e) => onChange({ ...mitarbeiter, role: e.target.value })}
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Stundensatz
            </label>
            <input
              type="number"
              step="0.01"
              value={mitarbeiter.stundensatz}
              onChange={(e) => onChange({ ...mitarbeiter, stundensatz: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Telefon
            </label>
            <input
              type="text"
              value={mitarbeiter.telefon}
              onChange={(e) => onChange({ ...mitarbeiter, telefon: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Mobil
            </label>
            <input
              type="text"
              value={mitarbeiter.mobil}
              onChange={(e) => onChange({ ...mitarbeiter, mobil: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Notizen
          </label>
          <textarea
            rows={3}
            value={mitarbeiter.notizen}
            onChange={(e) => onChange({ ...mitarbeiter, notizen: e.target.value })}
            className="input"
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={mitarbeiter.ist_aktiv_mitarbeiter}
            onChange={(e) => onChange({ ...mitarbeiter, ist_aktiv_mitarbeiter: e.target.checked })}
            style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
          />
          <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>
            Aktiv
          </span>
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="btn">
            <X size={18} /> Abbrechen
          </button>
          <button
            onClick={onSave}
            className="btn btn-primary"
            disabled={!mitarbeiter.first_name || !mitarbeiter.last_name || !mitarbeiter.username}
          >
            <Check size={18} /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

export default MitarbeiterView;
